import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const countPercentage = (value, count) => ((value ?? 0) / count) * 100;
const sumUp = (res, item) => res + item;

const formatData = (data) => {
    if (!data) return null;
    return data.map(item => ({
        text: item.text,
        value: Number(item.value?.toFixed(1)) || 0,
        ats: Array.isArray(item.ats) ? item.ats.map(a => ({
            name: a.name,
            val: Number(a.val?.toFixed(1)) || 0
        })) : []
    }));
};

export const useMatrixData = (klausimas, visiAtsakymai) => {
    const { i18n } = useTranslation();
    const pavadinimas = klausimas?.name;
    const lng = i18n.language === 'en' ? 'default' : i18n.language;

    const getResultsGrouped = useCallback((answ, answers) => {
        const item = answers[pavadinimas];
        if (!item) return answ;

        Object.keys(item).forEach(group => {
            if (!answ[group]) {
                answ[group] = {};
            }

            const value = item[group];
            if (value !== undefined && value !== null) {
                if (!answ[group][value]) {
                    answ[group][value] = 1;
                } else {
                    answ[group][value] += 1;
                }
            }
        });

        return answ;
    }, [pavadinimas]);

    const ats = useMemo(() => {
        if (!visiAtsakymai || !Array.isArray(visiAtsakymai)) return {};
        return visiAtsakymai.reduce(getResultsGrouped, {});
    }, [visiAtsakymai, getResultsGrouped]);

    const data = useMemo(() => {
        if (!klausimas?.rows || !klausimas?.columns) return null;

        return klausimas.rows.map(({ value, text }) => {
            if (!ats[value]) return { text: typeof text === 'object' ? text[lng] : text, ats: [] };

            const totalResponses = Object.values(ats[value]).reduce(sumUp, 0);
            const answers = klausimas.columns.map(item => {
                const count = ats[value][item.value] || 0;
                const percentage = countPercentage(count, totalResponses);
                return {
                    name: item.text[lng],
                    val: percentage
                };
            });

            return {
                text: typeof text === 'object' ? text[lng] : text,
                ats: answers,
                value: answers.reduce((sum, ans) => sum + ans.val, 0) / answers.length
            };
        });
    }, [klausimas, ats, lng]);

    const legend = useMemo(() => {
        if (!klausimas?.columns) return null;
        return klausimas.columns.map(col => ({
            name: typeof col.text === 'object' ? col.text[lng] : col.text,
            color: col.color || '#000'
        }));
    }, [klausimas, lng]);

    return { data: formatData(data), legend };
};

export const useRadiogroupData = (klausimas, visiAtsakymai) => {
    const { i18n } = useTranslation();
    const pavadinimas = klausimas?.name;
    const lng = i18n.language === 'en' ? 'default' : i18n.language;

    const getResultsGrouped = useCallback((answ, answers) => {
        const item = answers[pavadinimas];
        if (item === undefined || item === null) return answ;

        if (!answ[item]) {
            answ[item] = 1;
        } else {
            answ[item] += 1;
        }

        return answ;
    }, [pavadinimas]);

    const ats = useMemo(() => {
        if (!visiAtsakymai || !Array.isArray(visiAtsakymai)) return {};
        return visiAtsakymai.reduce(getResultsGrouped, {});
    }, [visiAtsakymai, getResultsGrouped]);

    const totalResponses = useMemo(() => 
        Object.values(ats).reduce((sum, count) => sum + count, 0),
        [ats]
    );

    const data = useMemo(() => {
        if (!klausimas?.choices) return null;

        return klausimas.choices.map(({ value, text }) => ({
            text: typeof text === 'object' ? text[lng] : text,
            value: countPercentage(ats[value], totalResponses),
            ats: [{
                name: typeof text === 'object' ? text[lng] : text,
                val: countPercentage(ats[value], totalResponses)
            }]
        }));
    }, [klausimas, ats, totalResponses, lng]);

    const legend = useMemo(() => {
        if (!klausimas?.choices) return null;
        return klausimas.choices.map(choice => ({
            name: typeof choice.text === 'object' ? choice.text[lng] : choice.text,
            color: choice.color || '#000'
        }));
    }, [klausimas, lng]);

    return { data: formatData(data), legend };
};
